import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications, callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import * as NavConstants from 'constants/NavConstants';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import * as ShortcutUtil from 'util/ShortcutUtil';

import * as FileBrowserDAO from './FileBrowserDAO';

export interface TagUpdateOptions {
  toSet: Record<string, Partial<SchemaTag>>;
  toAdd?: Array<string>;
  toRemove: Array<string>;
  toRename: Record<string, string>;
}

const FinderActions = {
  _bulkDeleteUsingMethod(
    deletionMethod: (
      fileIds: Array<string>,
      {parentFolderIds}: {parentFolderIds: Array<string>}
    ) => JQueryPromise<{}>,
    zippedArgs: Array<[string, string]>,
    finderId: string
  ): void {
    const {ids, parentFolderIds} = zippedArgs.reduce<{ids: Array<string>; parentFolderIds: Array<string>}>(
      ({ids, parentFolderIds}, [id, parentFolderId]) => {
        ids.push(id);
        parentFolderIds.push(parentFolderId);
        return {ids, parentFolderIds};
      },
      {ids: [], parentFolderIds: []}
    );
    deletionMethod(ids, {parentFolderIds})
      .done(() => {
        const section = NavConstants.FINDER_ID_TO_SECTION[finderId];
        getStore().dispatch(SearchUIReduxActions.refreshListing(section));
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_BROWSER_ITEMS_DELETED,
          ids,
        });
      })
      .fail((response) => {
        notifications.notifyError(response);
      });
  },

  bulkDelete(
    finderId: string,
    files: Array<ShortcutUtil.ShouldShowShortcutItem>,
    {parentFolderIds}: {parentFolderIds: Array<string>}
  ): void {
    // Partition arguments into shortcut and non-shortcut file lists
    const {zippedShortcutArgs, zippedNonShortcutArgs} = files.reduce<{
      zippedShortcutArgs: Array<[string, string]>;
      zippedNonShortcutArgs: Array<[string, string]>;
    }>(
      ({zippedShortcutArgs, zippedNonShortcutArgs}, file, index) => {
        const id = file.id;
        // FIXME: We can have multiple parents (in search results)
        const parentFolderId = parentFolderIds[index];
        const shouldShowShortcut = ShortcutUtil.shouldShowShortcut(file, finderId);
        if (shouldShowShortcut) {
          zippedShortcutArgs.push([id, parentFolderId]);
        } else {
          zippedNonShortcutArgs.push([id, parentFolderId]);
        }
        return {zippedShortcutArgs, zippedNonShortcutArgs};
      },
      {
        zippedShortcutArgs: [],
        zippedNonShortcutArgs: [],
      }
    );

    // Delete shortcuts and non-shortcuts independently
    this._bulkDeleteUsingMethod(FileBrowserDAO.deleteFiles, zippedNonShortcutArgs, finderId);
    this._bulkDeleteUsingMethod(FileBrowserDAO.deleteShortcuts, zippedShortcutArgs, finderId);
  },

  async updateTagsWithDataMods(
    ids: Array<string>,
    isRegistered: boolean,
    {toSet = {}, toAdd = [], toRemove = [], toRename = {}}: TagUpdateOptions,
    {onFailure}: {onFailure?: () => void} = {}
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> {
    let tags = null;
    await callAsyncWithDataModifications({
      changeSummary: isRegistered
        ? getInventoryRegistryChangeSummary({
            apiIds: ids,
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : '',
      onPerformAction: async (dataModsHeader) => {
        tags = await this.updateTags(ids, {toSet, toAdd, toRemove, toRename}, {onFailure}, dataModsHeader);
      },
      apiIds: ids,
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: ids.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && isRegistered,
    });
    return tags;
  },

  async updateTags(
    ids: Array<string>,
    {toSet = {}, toAdd = [], toRemove = [], toRename = {}}: TagUpdateOptions,
    {onFailure}: {onFailure?: () => void} = {},
    dataModsHeader?: DataModificationsHeader
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> {
    try {
      const data = await FileBrowserDAO.updateTags(
        ids,
        {
          toSet,
          toAdd,
          toRemove,
          toRename,
        },
        dataModsHeader
      );
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_TAGS_UPDATED,
        tagData: data.data,
      });
      notifications.success('Fields were updated successfully.');
      return data.data;
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
      if (onFailure) {
        onFailure();
      }
    }
    return null;
  },

  updateTagsWithFinderWithDataMods(
    finderId: string,
    ids: Array<string>,
    hasRegisteredItems: boolean,
    {toSet, toAdd, toRemove, toRename}: TagUpdateOptions
  ): void {
    callWithDataModifications({
      changeSummary: hasRegisteredItems
        ? getInventoryRegistryChangeSummary({
            apiIds: ids,
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : '',
      onPerformAction: (dataModsHeader) => {
        this.updateTagsWithFinder(finderId, ids, {toSet, toAdd, toRemove, toRename}, dataModsHeader);
      },
      apiIds: ids,
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: ids.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic:
        BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && hasRegisteredItems,
    });
  },

  // TODO(T6458): make passing the finderId the default behavior in updateTags and get rid of this function
  // Updates the finder with updated values, allowing for UI refreshes of new info without a refetch
  updateTagsWithFinder(
    finderId: string,
    ids: Array<string>,
    {toSet, toAdd, toRemove, toRename}: TagUpdateOptions,
    dataModsHeader: DataModificationsHeader | undefined
  ): void {
    FileBrowserDAO.updateTags(ids, {toSet, toAdd, toRemove, toRename}, dataModsHeader)
      .then(({data}) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_TAGS_WITH_FINDER_UPDATED,
          finderId,
          tagData: data,
        });

        const fileCacheUpdates = data
          .map((fileWithTags) => {
            const {id, tags} = fileWithTags;
            const cachedBioentity = readFragmentFromCache(getFragmentWrapper('BioentitySummary'), id);
            return cachedBioentity ? {...cachedBioentity, tagsJson: tags} : null;
          })
          .filter((cacheUpdate) => cacheUpdate != null);
        writeFragmentsToCache(getFragmentWrapper('BioentitySummary'), fileCacheUpdates);

        notifications.success('Fields were updated successfully.');
      })
      .catch(notifications.notifyError);
  },
};

export default FinderActions;
